import { useState } from 'react';
import { CHAIN_LABELS, isNativeToken } from '@incpt/shared';
import { useTranslation } from 'react-i18next';
import { lottie } from '../../assets/figma/lottie';
import { TransactionDetailSheet } from './TransactionDetailSheet';
import type { TransferDto, TransferStatus, Chain, Token } from '../../lib/transfers';
import type { SwapOrderDto } from '../../lib/swap';
import type { IncptTransferDto } from '../../lib/incpt';
import { isSwapPending } from '../../lib/swap';
import { formatTokenAmount, type DisplaySymbol } from '../../lib/format';

export type TxType = 'receive' | 'send' | 'topup' | 'swap' | 'error' | 'comission';

export type Tx = {
  direction: 'in' | 'out';
  asset: string;
  network: string;
  date: string;
  amount: string;
  kind?: 'card' | 'crypto' | 'swap';
  label?: string;
  type?: TxType;
  // Real-transfer detail (present for live transfers; absent for mock/demo rows).
  status?: TransferStatus | 'pending' | 'submitted' | 'success' | 'failed';
  /** ChangeHero / swap lifecycle when kind === 'swap'. */
  swapStatus?: string;
  /** Server reason on a swap row (`provider_unseen` once the deposit confirmed). */
  swapStatusReason?: string | null;
  swapPair?: string; // e.g. "USDT → BTC"
  swapFromAmount?: string;
  swapToAmount?: string;
  swapToToken?: string;
  partnerOrderId?: string;
  chain?: Chain; // raw chain for actions (Resend)
  token?: Token;
  tokenAmount?: string; // bare token amount, e.g. "3.5"
  feeUsd?: string; // "1.50"
  feePct?: string; // bank/FX fee as a % of the merchant amount, e.g. "1.76"
  counterparty?: string; // recipient (out) / sender (in) address
  /** Wallet the transfer was made from (list shows first+last 3 chars). */
  fromAddress?: string;
  txHash?: string | null;
  failureReason?: string | null; // 'insufficient_energy' → Resend with higher energy
  fullDate?: string; // long date for the detail dialog
  sortTs?: number; // epoch ms — canonical sort key across transfers + card txs
  merchant?: string;
  /** Raw Banana card tx type (`recharge`, `withdraw`, `settlement_debit`, …). */
  cardType?: string;
  debitAmount?: string; // full card debit in USDT
  paymentAmount?: string; // merchant check in USDT when no local-currency amount exists
  localAmount?: string; // merchant's local charge for card txs, e.g. "100.00 PLN"
  bankFeeUsd?: string;
  forexFeeUsd?: string;
  /** Purchase the merchant refunded — excluded from the spending totals. */
  cardRefunded?: boolean;
};

const NETWORK_LABEL: Record<Chain, string> = {
  eth: 'ERC-20',
  trx: 'TRC-20',
  bsc: 'BEP-20',
  sol: 'SPL',
  hyperevm: 'HyperEVM',
  // TON's token standard. Not "TON" — that is the coin, and a native send is
  // labelled with the chain name below rather than with a standard.
  ton: 'Jetton',
  btc: 'Bitcoin',
  zec: 'Zcash',
  xmr: 'Monero',
  doge: 'Dogecoin',
  xrp: 'XRP Ledger',
};

/** Token-aware network label: a coin shows the chain name, not a token-standard
 *  label (a native ETH transfer is NOT "ERC-20"). Asked of the token rather
 *  than listed coin by coin, which left each new coin labelled with its chain's
 *  token standard — a BNB send reading "BEP-20". */
function networkLabel(chain: Chain, token: string): string {
  if (isNativeSymbol(token)) return CHAIN_LABELS[chain];
  return NETWORK_LABEL[chain];
}

const isNativeSymbol = (token: string) => isNativeToken(token);

/** Display amount: symbol-aware decimals (USDT 2, ETH 6, BTC 8, TRX 6, INCPT 2). */
function tidyAmount(amount: string, symbol?: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  if (
    symbol === 'USDT' ||
    symbol === 'USDC' ||
    symbol === 'ETH' ||
    symbol === 'BTC' ||
    symbol === 'TRX'
  ) {
    return formatTokenAmount(n, symbol as DisplaySymbol);
  }
  // INCPT and other off-chain / unknown symbols — keep the list readable.
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function dateLocale(locale: string): string {
  switch (locale) {
    case 'ru':
      return 'ru-RU';
    case 'uk':
      return 'uk-UA';
    case 'pl':
      return 'pl-PL';
    case 'es':
      return 'es-ES';
    case 'tr':
      return 'tr-TR';
    case 'el':
      return 'el-GR';
    case 'it':
      return 'it-IT';
    case 'de':
      return 'de-DE';
    case 'pt':
      return 'pt-PT';
    default:
      return 'en-US';
  }
}

function formatTxDate(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleString(dateLocale(locale), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFullDate(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleString(dateLocale(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Compact source-wallet hint for the history list: first 3 + last 3. */
function shortFromWallet(a: string): string {
  return a.length > 6 ? `${a.slice(0, 3)}…${a.slice(-3)}` : a;
}

/** Canonical card-transaction row from the API (`GET /cards/:id/transactions`).
 *  The server normalizes Banana's ConsoleTransactionOut + stored history into this. */
export type CardTxDto = {
  id: string;
  type: string; // 'recharge' | 'consume' | 'refund' | 'withdraw' | …
  merchant: string | null;
  amount: string;
  currency: string;
  status: string;
  ts: number | null; // epoch ms
  /** Full amount debited from the card, including collected fees. */
  debitAmount?: string | null;
  /** Issuer bank fee included in the billing amount. */
  bankFeeUsd?: string | null;
  /** Cross-border/FX fee reported separately by Banana. */
  forexFeeUsd?: string | null;
  /** The merchant refunded this purchase — it is not spending. */
  refunded?: boolean;
  /** Merchant-purchase bank/FX commission (billing − check). Shown in detail only. */
  feeUsd?: string | null;
  /** That commission as a % of the merchant amount, e.g. "1.76". */
  feePct?: string | null;
  /** Original merchant charge in the local currency (e.g. "100.00"), when different. */
  localAmount?: string | null;
  /** Local currency code for `localAmount`, e.g. "PLN". */
  localCurrency?: string | null;
};

function normCardTxType(type: string): string {
  return type.toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Card → wallet (or forced) balance leave. Checked before the loose "refund"
 * match: Banana's CARD_REFUND is a transfer OUT, not a purchase refund.
 */
function isCardTxTransferOut(type: string): boolean {
  const t = normCardTxType(type);
  return (
    t === 'withdraw' ||
    t === 'withdrawal' ||
    t === 'card_refund' ||
    t === 'compulsory_liquidation' ||
    /(?:^|_)withdraw(?:al)?(?:_|$)/.test(t)
  );
}

/** Card tx types that credit the card (incoming). Everything else is a debit. */
export function isCardTxIncoming(type: string): boolean {
  if (isCardTxTransferOut(type)) return false;
  return /recharge|refund|credit|topup|top_up|deposit|reversal|cashback/.test(
    normCardTxType(type),
  );
}

/**
 * Cards-tab turnover: "topped up" = user recharges only; "spent" = merchant
 * debits / fees. Withdrawals and purchase refunds are excluded from both.
 */
export function cardTxTurnover(type: string): 'topup' | 'spend' | null {
  const t = normCardTxType(type);
  if (!t || isCardTxTransferOut(t)) return null;
  if (/recharge|top_?up|(?:^|_)deposit(?:_|$)/.test(t) && !/refund/.test(t)) return 'topup';
  if (/refund|reversal|cashback|settlement_credit|(?:^|_)credit(?:_|$)/.test(t)) return null;
  return 'spend';
}

/** Localized label for a card tx type (falls back to the raw type, capitalized). */
function cardTypeLabel(type: string, t: (k: string) => string): string {
  const key = `cards.txType.${type}`;
  const label = t(key);
  if (label !== key) return label; // i18n had a translation
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : t('cards.txType.default');
}

/** Map a canonical card transaction into the shared Tx row shape. */
export function cardTxToTx(tx: CardTxDto, locale = 'en', t?: (k: string) => string): Tx {
  const declined = /declin|fail|reject|cancel/.test(tx.status);
  const pending = /pending|processing/i.test(tx.status);
  const incoming = !declined && isCardTxIncoming(tx.type);
  const direction: 'in' | 'out' = incoming ? 'in' : 'out';

  let type: TxType = declined ? 'error' : incoming ? 'receive' : 'send';
  if (/recharge|topup|top_up|deposit/.test(tx.type) && !/refund/.test(tx.type)) type = 'topup';

  // Keep the list label separate from the detail currency.
  const label = tx.merchant?.trim() || (t ? cardTypeLabel(tx.type, t) : tx.type || 'Card');

  const amtNum = Math.abs(Number(tx.amount));
  const amt = Number.isFinite(amtNum) && amtNum > 0 ? String(amtNum) : '';
  const cur = (tx.currency || 'USD').toUpperCase();
  const isUsd = cur === 'USD' || cur === 'USDT';
  const debitNum = Math.abs(Number(tx.debitAmount));
  const hasDebit = tx.debitAmount != null && Number.isFinite(debitNum) && debitNum > 0;
  // Hero / list unit follows display currency; USD debit stays on `debitAmount`
  // for detail + turnover stats (local PLN row must not show the USD figure as PLN).
  const tokenAmount = isUsd ? (hasDebit ? tx.debitAmount! : amt) : amt;
  const sign = incoming ? '+' : '-';
  const iso = tx.ts != null ? new Date(tx.ts).toISOString() : '';
  // A status row can omit `amount` while the USD debit is known. Show that debit
  // instead of "—". Never substitute it on a local-currency row.
  const shown = amt || (isUsd && hasDebit ? String(debitNum) : '');
  const listAmount = !shown
    ? '—'
    : isUsd
      ? `${sign}$${shown}`
      : `${sign}${shown} ${cur}`;

  return {
    direction,
    kind: 'card',
    // Detail hero unit: USD family → USDT label; else the local ISO code.
    asset: isUsd ? 'USDT' : cur,
    network: t ? t('cardSettings.title') : 'Card',
    date: iso ? formatTxDate(iso, locale) : '',
    fullDate: iso ? formatFullDate(iso, locale) : '',
    amount: listAmount,
    tokenAmount,
    type,
    status: declined ? 'failed' : pending ? 'pending' : 'success',
    label,
    merchant: tx.merchant?.trim() || undefined,
    cardType: tx.type,
    paymentAmount: shown || undefined,
    sortTs: tx.ts ?? undefined,
    ...(hasDebit ? { debitAmount: tx.debitAmount! } : {}),
    ...(tx.bankFeeUsd != null ? { bankFeeUsd: tx.bankFeeUsd } : {}),
    ...(tx.forexFeeUsd != null ? { forexFeeUsd: tx.forexFeeUsd } : {}),
    ...(tx.refunded ? { cardRefunded: true } : {}),
    ...(tx.feeUsd ? { feeUsd: tx.feeUsd, ...(tx.feePct ? { feePct: tx.feePct } : {}) } : {}),
    ...(tx.localAmount && tx.localCurrency
      ? { localAmount: `${tx.localAmount} ${tx.localCurrency}` }
      : {}),
  };
}

/** Map an off-chain INCPT ledger entry into the UI transaction-row shape. */
export function incptLedgerToTx(
  e: IncptTransferDto,
  locale = 'en',
  t?: (k: string, opts?: Record<string, unknown>) => string,
): Tx {
  const incoming =
    e.kind === 'transfer_in' ||
    e.kind === 'admin_grant' ||
    e.kind === 'promocode' ||
    e.kind === 'referral' ||
    e.kind === 'purchase';
  const sign = incoming ? '+' : '-';
  const amt = tidyAmount(e.amount, 'INCPT');
  const ts = new Date(e.createdAt).getTime();

  let label: string;
  let type: TxType;
  if (e.kind === 'transfer_out') {
    label = t?.('incpt.tx.send') ?? 'INCPT sent';
    type = 'send';
  } else if (e.kind === 'transfer_in') {
    label = t?.('incpt.tx.receive') ?? 'INCPT received';
    type = 'receive';
  } else if (e.kind === 'admin_grant') {
    label = t?.('incpt.tx.grant') ?? 'INCPT grant';
    type = 'receive';
  } else if (e.kind === 'promocode') {
    label = t?.('incpt.tx.promocode') ?? 'Promocode';
    type = 'receive';
  } else if (e.kind === 'referral') {
    label = t?.('incpt.tx.referral') ?? 'Referral reward';
    type = 'receive';
  } else {
    label = t?.('incpt.tx.purchase') ?? 'INCPT purchase';
    type = 'topup';
  }

  const counterparty =
    e.counterpartyNickname?.trim() ||
    (e.counterpartyTgUserId ? `ID ${e.counterpartyTgUserId}` : undefined);

  return {
    direction: incoming ? 'in' : 'out',
    kind: 'crypto',
    asset: 'INCPT',
    label,
    network: 'TRC20',
    date: formatTxDate(e.createdAt, locale),
    amount: `${sign}${amt} INCPT`,
    type,
    status: 'success',
    tokenAmount: amt,
    feeUsd: '0.00',
    counterparty,
    fullDate: formatFullDate(e.createdAt, locale),
    sortTs: ts,
  };
}

/** Presale purchase from `/v1/presale` — same row chrome as ledger entries. */
export function presalePurchaseToTx(
  p: { tokens: number; createdAt: string },
  locale = 'en',
  t?: (k: string) => string,
): Tx {
  const amt = tidyAmount(String(p.tokens), 'INCPT');
  return {
    direction: 'in',
    kind: 'crypto',
    asset: t?.('presale.historyItem') ?? 'Presale purchase',
    network: 'TRC20',
    date: formatTxDate(p.createdAt, locale),
    amount: `+${amt} INCPT`,
    type: 'topup',
    status: 'success',
    tokenAmount: amt,
    feeUsd: '0.00',
    fullDate: formatFullDate(p.createdAt, locale),
    sortTs: new Date(p.createdAt).getTime(),
  };
}

/** Map a backend transfer record into the UI's transaction-row shape.
 *  Returns an array: card_payment out-transfers produce two rows (card issue + network fee). */
export function transferToTx(tr: TransferDto, locale = 'en', t?: (k: string) => string): Tx[] {
  const incoming = tr.direction === 'in';
  const sign = incoming ? '+' : '-';
  const amt = tidyAmount(tr.amount);
  const ts = new Date(tr.createdAt).getTime();

  const isCardPayment = !incoming && tr.purpose === 'card_payment' && tr.status === 'success';
  const isPresalePayment =
    !incoming && tr.purpose === 'presale_payment' && tr.status === 'success';
  const fee = Number(tr.feeUsd);

  const mainType: TxType = isCardPayment || isPresalePayment
    ? 'topup'
    : tr.status === 'failed'
      ? 'error'
      : incoming
        ? 'receive'
        : 'send';
  const mainAsset = isPresalePayment
    ? (t?.('wallet.txPresale') ?? 'INCPT presale')
    : isCardPayment
      ? tr.cardPaymentKind === 'topup'
        ? (t?.('wallet.txCardTopUp') ?? 'Card top-up')
        : (t?.('wallet.txCardIssue') ?? 'Card issue')
      : tr.token;

  // Stablecoins render as USD ("-$12.5"); natives render in-kind ("-0.001 ETH").
  const native = isNativeSymbol(tr.token);
  const main: Tx = {
    direction: tr.direction,
    kind: 'crypto',
    asset: mainAsset,
    network: networkLabel(tr.chain, tr.token),
    date: formatTxDate(tr.createdAt, locale),
    amount: native ? `${sign}${amt} ${tr.token}` : `${sign}$${amt}`,
    type: mainType,
    status: tr.status,
    chain: tr.chain,
    token: tr.token,
    tokenAmount: amt,
    // Natives carry no service fee — 0 renders as no fee line in the detail sheet.
    feeUsd: fee > 0 ? fee.toFixed(2) : '0.00',
    counterparty: incoming ? tr.fromAddress : tr.toAddress,
    fromAddress: tr.fromAddress,
    txHash: tr.txHash,
    failureReason: tr.failureReason,
    fullDate: formatFullDate(tr.createdAt, locale),
    sortTs: ts,
  };

  if ((!isCardPayment && !isPresalePayment) || fee <= 0) return [main];

  const feeLabel = t?.('wallet.txCommission') ?? 'Network fee';
  const feeRow: Tx = {
    direction: 'out',
    kind: 'crypto',
    asset: feeLabel,
    network: networkLabel(tr.chain, tr.token),
    date: formatTxDate(tr.createdAt, locale),
    amount: `-$${fee.toFixed(2)}`,
    type: 'comission',
    status: tr.status,
    chain: tr.chain,
    token: tr.token,
    tokenAmount: fee.toFixed(2),
    counterparty: tr.toAddress,
    fromAddress: tr.fromAddress,
    txHash: tr.txHash,
    fullDate: formatFullDate(tr.createdAt, locale),
    sortTs: ts + 1,
  };

  return [main, feeRow];
}

/** Map a ChangeHero SwapOrder into a single history row (type: swap). */
export function swapOrderToTx(
  order: SwapOrderDto,
  locale = 'en',
  t?: (k: string) => string,
  depositTxHash?: string | null,
): Tx {
  const pending = isSwapPending(order.status);
  const failed = order.status === 'FAILED' || order.status === 'REFUNDED';
  const toRaw = order.toAmount != null && Number(order.toAmount) > 0 ? order.toAmount : null;
  const toAmt = toRaw ? tidyAmount(toRaw, order.toToken) : null;
  const fromAmt = tidyAmount(order.fromAmount, order.fromToken);
  const finished = order.status === 'FINISHED' && !!toAmt;

  let uiStatus: TransferStatus = 'success';
  if (failed) uiStatus = 'failed';
  else if (pending) uiStatus = 'pending';

  // Hero amount/asset must match: pending shows what you gave; finished shows what you got.
  const heroToken = finished ? order.toToken : order.fromToken;
  const heroChain = finished ? order.toChain : order.fromChain;
  const heroAmount = finished ? toAmt! : fromAmt;

  const fromNet = networkLabel(order.fromChain, order.fromToken);
  const toNet = networkLabel(order.toChain, order.toToken);
  // Row pill must stay short so "Обмен" never truncates; detail keeps the full pair.
  const pairFull =
    order.fromToken === order.toToken
      ? `${order.fromToken} ${fromNet} → ${order.toToken} ${toNet}`
      : `${order.fromToken} → ${order.toToken}`;
  const pairShort =
    order.fromToken === order.toToken ? `${fromNet} → ${toNet}` : `${order.fromToken} → ${order.toToken}`;

  return {
    direction: finished ? 'in' : 'out',
    kind: 'swap',
    type: 'swap',
    asset: heroToken,
    network: pairShort,
    date: formatTxDate(order.createdAt, locale),
    amount: finished ? `+${toAmt} ${order.toToken}` : `−${fromAmt} ${order.fromToken}`,
    label: t?.('swap.txLabel') ?? 'Swap',
    status: uiStatus,
    swapStatus: order.status,
    swapStatusReason: order.statusReason,
    swapPair: pairFull,
    swapFromAmount: `${fromAmt} ${order.fromToken}`,
    swapToAmount: toAmt ?? undefined,
    swapToToken: order.toToken,
    partnerOrderId: order.partnerOrderId,
    txHash: depositTxHash ?? null,
    chain: heroChain,
    token: heroToken,
    tokenAmount: heroAmount,
    counterparty: order.payinAddress ?? undefined,
    fullDate: formatFullDate(order.createdAt, locale),
    sortTs: new Date(order.createdAt).getTime(),
  };
}

/** Localized status line for a row: Failed / Pending / Received / Sent / swapped. */
export function txStatusLabel(tx: Tx, t: (k: string) => string): string {
  if (tx.type === 'swap' || tx.kind === 'swap') {
    const s = (tx.swapStatus ?? '').toUpperCase();
    if (s === 'FINISHED') return t('swap.statusFinished');
    if (s === 'FAILED') return t('swap.statusFailed');
    if (s === 'REFUNDED') return t('swap.statusRefunded');
    if (tx.swapStatusReason === 'provider_unseen') return t('swap.statusUnseen');
    return t('swap.statusPending');
  }
  if (tx.status === 'failed') return t('wallet.failed');
  if (tx.status === 'pending' || tx.status === 'submitted') return t('wallet.pending');
  return tx.direction === 'in' ? t('wallet.received') : t('wallet.sent');
}

const TX_LOTTIE: Record<TxType, unknown> = {
  receive: lottie.txReceive,
  send: lottie.txSend,
  topup: lottie.txTopup,
  swap: lottie.txSwap,
  error: lottie.txError,
  comission: lottie.txComission,
};

/** Lottie animation for a transaction, by its type (falls back to direction). */
export function txLottie(tx: Tx): unknown {
  return TX_LOTTIE[tx.type ?? (tx.direction === 'in' ? 'receive' : 'send')];
}

export const MOCK_TXS: Tx[] = [
  { direction: 'in', type: 'receive', asset: 'USDT', network: 'TRC-20', date: 'Сегодня, 16:52', amount: '+$500' },
  { direction: 'in', type: 'swap', asset: 'USDT', network: 'TRC-20', date: 'Сегодня, 16:52', amount: '+$500' },
  { direction: 'in', type: 'topup', asset: 'USDT', network: 'TRC-20', date: 'Сегодня, 16:52', amount: '+$500' },
];

/** Static list icon. Lottie is reserved for the detail modal that the user
 * actually opens; mounting an SVG animation renderer for every closed row (and
 * another one inside every hidden detail modal) exhausted Telegram iOS WebView. */
function TransactionStaticIcon({ tx }: { tx: Tx }) {
  const failed = tx.status === 'failed' || tx.type === 'error';
  const incoming = tx.direction === 'in';

  if (failed) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="#ff3b30" strokeWidth="1.8" />
        <path d="m9 9 6 6m0-6-6 6" stroke="#ff3b30" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (tx.type === 'swap') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M7 7h10l-2.5-2.5M17 17H7l2.5 2.5" stroke="#377dff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={incoming ? 'M7 7l10 10m0-6v6h-6' : 'M17 17 7 7m0 6V7h6'}
        stroke={incoming ? '#34c759' : '#377dff'}
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TransactionRow({ tx, light }: { tx: Tx; light?: boolean }) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const incoming = tx.direction === 'in';
  return (
    <>
    <button
      onClick={() => setDetailOpen(true)}
      className={`flex items-center gap-3 rounded-[24px] p-[14px] text-left ${
        light
          ? 'bg-[#f6f7f8]'
          : 'border border-white/60 bg-white/45 shadow-[0_10px_24px_-12px_rgba(17,24,39,0.16)] backdrop-blur-xl'
      }`}
    >
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full ${
          light ? 'bg-white' : 'bg-white/65 backdrop-blur-md'
        }`}
      >
        <TransactionStaticIcon tx={tx} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-[16px] font-medium text-ink">
            {tx.label ?? tx.asset}
          </span>
          <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-medium text-muted">
            {tx.network}
          </span>
        </div>
        <span className="truncate text-[12px] text-muted">
          {tx.date}
          {tx.fromAddress ? ` · ${shortFromWallet(tx.fromAddress)}` : ''}
        </span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2 text-right">
        <span className={`text-[16px] font-medium tabular-nums ${incoming ? 'text-positive' : 'text-ink'}`}>
          {tx.amount}
        </span>
        <span
          className={`text-[12px] ${tx.status === 'failed' ? 'text-[#ff3b30]' : 'text-muted'}`}
        >
          {txStatusLabel(tx, t)}
        </span>
      </div>
    </button>
    {detailOpen && (
      <TransactionDetailSheet open onClose={() => setDetailOpen(false)} tx={tx} />
    )}
    </>
  );
}
